import React, { useMemo, useState, useRef, useEffect, useContext } from 'react';
import { Alert, AlertTitle, Box, Chip, Grid, Skeleton, Stack, Typography, useTheme, FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Checkbox, Slider, Button, Divider, IconButton, Paper, ToggleButton, ToggleButtonGroup, Card, CardContent } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
// New modular components
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { UserContext } from '../../components/shared/useUser';
import { TimeRangeBar } from './components/TimeRangeBar';
import { useDashboardOperationalData, StoreContext } from './components/useDashboardOperationalData';
import { useDashboardActionItems } from './components/useDashboardActionItems';
import { useDashboardPerformanceData } from './components/useDashboardPerformanceData';
import { useDashboardNews } from './hooks/useDashboardNews';
import { useLocalOperationalEvents } from './hooks/useLocalOperationalEvents';
import { TimeRange } from './components/types';
import { UrgentAlertCard } from './components/UrgentAlertCard';
import { TasksCard, TaskFilterType } from './components/TasksCard';
import { ProgressCard } from './components/ProgressCard';
import { MetricCard } from './components/MetricCard';
import { WaveScoreCard } from './components/WaveScoreCard';
import { NewsCarousel } from './components/NewsCarousel';
import { isDemoModeEnabled, isStoreSpecificPersona } from '../../utils/featureAccess';

// -------------------- Props for host app wiring --------------------
interface Props {
  selectedStore?: { storeName?: string; storeNumber?: string; franchiseName?: string } | null;
  user?: { username?: string; emailAddress?: string } | null;
  hasPermissionTo?: (perms: string[]) => boolean;
  isCreditRequestEnabled?: boolean;
  t?: (key: string) => string;
  theme?: { brandingImages?: { dashboardImage?: string } };
}

// -------------------- Page --------------------
export default function OrderlyUnifiedDashboard(props: Props) {
  const theme = useTheme();
  const { selectedStore: contextSelectedStore, selectedFranchise, demoPersona, hasPermissionTo } = useContext(UserContext);
  // Use store from context if props not provided (for standalone usage)
  const selectedStore = props.selectedStore || contextSelectedStore;
  const [range, setRange] = useState<TimeRange>('7d');
  // Simulation state
  const [simAnchorEl, setSimAnchorEl] = useState<HTMLElement | null>(null);
  type StoreType = 'chicken_qsr' | 'coffee_shop' | 'italian_restaurant' | 'burger_qsr' | 'dessert_qsr' | 'steak_restaurant' | 'carvery_restaurant' | 'cocktail_bar' | 'pub';
  const storeTypeOptions: { value: StoreType; label: string }[] = [
    { value: 'chicken_qsr', label: 'Chicken QSR' },
    { value: 'coffee_shop', label: 'Coffee Shop' },
    { value: 'italian_restaurant', label: 'Italian Restaurant' },
    { value: 'burger_qsr', label: 'Burger QSR' },
    { value: 'dessert_qsr', label: 'Dessert QSR' },
    { value: 'steak_restaurant', label: 'Steak Restaurant' },
    { value: 'carvery_restaurant', label: 'Carvery Restaurant' },
    { value: 'cocktail_bar', label: 'Cocktail Bar' },
    { value: 'pub', label: 'Pub' },
  ];
  const storeTypeLabel = (v: StoreType) => storeTypeOptions.find(o => o.value === v)?.label || v;
  const [storeType, setStoreType] = useState<StoreType>('chicken_qsr');

  const [events, setEvents] = useState<string[]>([]);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(4); // hours per real second
  const [currentHour, setCurrentHour] = useState<number>(8); // start morning
  const [playing, setPlaying] = useState<boolean>(false);
  const [simulationMode, setSimulationMode] = useState<boolean>(false);
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const [taskFilter, setTaskFilter] = useState<TaskFilterType[]>(['store', 'user']);
  const simTimerRef = useRef<number | null>(null);

  // Demo Role Picker personas that aren't scoped to a single store (Procurement/Compliance/
  // Commercial/Head Office operate across the estate) shouldn't default to a store-scoped task
  // view - the "Store" filter is disabled and the list defaults to the user's own tasks.
  const storeFilterDisabled = isDemoModeEnabled() && !isStoreSpecificPersona(demoPersona);

  useEffect(() => {
    setTaskFilter(storeFilterDisabled ? ['user'] : ['store', 'user']);
  }, [storeFilterDisabled]);

  // Derive effective storeType from selected franchise when not manually overridden in simulation
  const effectiveStoreType: StoreType = useMemo(() => {
    if (simulationMode) return storeType;
    const franchise = selectedStore?.franchiseName?.toLowerCase() || '';
    if (franchise.includes('vapiano')) return 'italian_restaurant';
    if (franchise.includes('five guys')) return 'burger_qsr';
    return storeType;
  }, [simulationMode, storeType, selectedStore?.franchiseName]);

  // Advance simulated time
  useEffect(() => {
    if (!simulationMode || !playing) {
      if (simTimerRef.current) window.clearInterval(simTimerRef.current);
      return;
    }
    simTimerRef.current = window.setInterval(() => {
      setCurrentHour(h => {
        const nh = h + playbackSpeed * (1 / 60); // advance fraction of hour each second
        return nh >= 24 ? 0 : nh;
      });
    }, 1000);
    return () => { if (simTimerRef.current) window.clearInterval(simTimerRef.current); };
  }, [simulationMode, playing, playbackSpeed]);

  // Build store context for API calls (only when not in simulation mode)
  const storeContext: StoreContext | undefined = useMemo(() => {
    if (simulationMode || !selectedStore?.storeNumber) {
      console.log('[DashboardV2] No store context:', { simulationMode, storeNumber: selectedStore?.storeNumber });
      return undefined;
    }
    const context = {
      storeNumber: selectedStore.storeNumber,
      franchiseName: selectedStore.franchiseName || 'default',
    };
    console.log('[DashboardV2] Store context created:', context);
    return context;
  }, [simulationMode, selectedStore?.storeNumber, selectedStore?.franchiseName]);

  const operational = useDashboardOperationalData(
    range,
    simulationMode ? { simulation: true, storeType: effectiveStoreType, currentHour, events } : undefined,
    storeContext,
  );
  const performance = useDashboardPerformanceData(range, { simulation: true, storeType: effectiveStoreType, currentHour: simulationMode ? currentHour : new Date().getHours() });
  const canConsumeNews = hasPermissionTo(['NewsConsume']);
  const news = useDashboardNews(
    storeContext ? { storeNumber: storeContext.storeNumber, franchiseId: selectedFranchise?.franchiseId ?? contextSelectedStore?.franchiseId } : undefined,
    canConsumeNews,
  );
  const actionItems = useDashboardActionItems();
  const localOperationalTasks = useLocalOperationalEvents(isDemoModeEnabled());
  const headerCopy = useMemo(() => 'Together we reduced food waste by 21% last month and saved £3,914 🚀', []);

  // Derive primary urgent task (from explicit alert, else from tasks by priority then scheduledAt)
  const { primaryTask, remainingTasks } = useMemo(() => {
    const allTasks = [...localOperationalTasks, ...(operational.tasks || [])];
    // Prefer explicit alert provided
    let primary = operational.alert || null;
    if (!primary) {
      primary = [...allTasks]
        .sort((a, b) => {
          const pa = (b.priority ?? 0) - (a.priority ?? 0);
          if (pa !== 0) return pa;
          const sa = a.scheduledAt ? Date.parse(a.scheduledAt) : Infinity;
          const sb = b.scheduledAt ? Date.parse(b.scheduledAt) : Infinity;
          return sa - sb;
        })[0] || null;
    }
    const remaining = primary ? allTasks.filter(t => t.id !== primary!.id) : allTasks;
    // Demo Mode only: append cross-domain action items (budget/supplier/compliance/rollout
    // approvals) after the real task feed - never replaces or reorders real tasks.
    return { primaryTask: primary, remainingTasks: [...remaining, ...actionItems] };
  }, [operational.alert, operational.tasks, actionItems, localOperationalTasks]);

  // Remove Popover anchor logic
  const toggleSimulation = () => { setPanelOpen(o => !o); };

  // Real-time feed status placeholders
  const realtimeFeeds = useMemo(() => [
    { name: 'POS', status: 'connected' },
    { name: 'Inventory', status: 'connected' },
    { name: 'Waste Scales', status: 'delayed' },
    { name: 'Weather', status: 'connected' },
  ], []);

  const eventOptions = ['football_match', 'heatwave', 'road_closure', 'supplier_delay'];

  const chipLabel = simulationMode ? `Simulation: ${storeTypeLabel(storeType)} @ ${currentHour.toFixed(1)}h` : 'Real time data integrations';

  return (
    <>
      <PageArea>
        <Stack direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}>
          <PageTitle subTitle={headerCopy}>Dashboard</PageTitle>
          <Chip size="small"
            color={simulationMode ? 'warning' : 'success'}
            onClick={() => { toggleSimulation(); }}
            label={chipLabel} />
        </Stack>
        {panelOpen && (
          <Card variant="outlined"
            sx={{ p: 2, mb: '-24px', mt: '24px', borderRadius: 3, backgroundColor: theme.palette.custom.gray[100] }}>
            <CardContent>
              <Stack direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}>
                <Typography variant="textLG"
                  fontWeight={700}
                  sx={{ fontSize: 16 }}>Data Mode</Typography>
                <ToggleButtonGroup size="small"
                  exclusive
                  value={simulationMode ? 'sim' : 'real'}
                  onChange={(_, v) => {
                    if (!v) return; // ignore deselect
                    const isSim = v === 'sim';
                    setSimulationMode(isSim);
                    if (!isSim) { setPlaying(false); }
                  }}>
                  <ToggleButton value="real">Real-Time</ToggleButton>
                  <ToggleButton value="sim">Simulated</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {!simulationMode && (
                <Stack spacing={2}>
                  <Typography variant="textMD"
                    sx={{ fontWeight: 700, mb: 2 }}>Feed Status</Typography>
                  <Stack direction="row"
                    flexWrap="wrap"
                    gap={2}>
                    {realtimeFeeds.map(f => (
                      <Chip key={f.name}
                        size="small"
                        label={`${f.name}: ${f.status}`}
                        color={f.status === 'connected' ? 'success' : f.status === 'delayed' ? 'warning' : 'default'} />
                    ))}
                  </Stack>
                  <Typography variant="textSM"
                    color="text.secondary">These statuses reflect live ingestion health.</Typography>
                </Stack>
              )}
              {simulationMode && (
                <Box>
                  {/* Simulation settings previously inside Popover */}
                  <FormControl fullWidth
                    size="small"
                    sx={{ mb: 2 }}>
                    <InputLabel id="store-type-label">Store Type</InputLabel>
                    <Select labelId="store-type-label"
                      value={storeType}
                      label="Store Type"
                      onChange={(e) => setStoreType(e.target.value as StoreType)}>
                      {storeTypeOptions.map(o => <MenuItem key={o.value}
                        value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography variant="textSM"
                    sx={{ fontWeight: 600 }}>Events</Typography>
                  <FormGroup row>
                    {eventOptions.map(evt => (
                      <FormControlLabel key={evt}
                        control={<Checkbox size="small"
                          checked={events.includes(evt)}
                          onChange={(e) => setEvents(prev => e.target.checked ? [...prev, evt] : prev.filter(p => p !== evt))} />}
                        label={evt.replace('_', ' ')} />
                    ))}
                  </FormGroup>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={1}>
                    <IconButton size="small"
                      onClick={() => setPlaying(p => !p)}>
                      {playing ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <Typography variant="textSM"
                      sx={{ width: 36 }}>{currentHour.toFixed(1)}h</Typography>
                    <Slider size="small"
                      min={0}
                      max={24}
                      step={0.25}
                      value={currentHour}
                      onChange={(_, v) => setCurrentHour(v as number)}
                      sx={{ flex: 1 }} />
                    <Typography variant="textSM">Speed</Typography>
                    <Select size="small"
                      value={playbackSpeed}
                      onChange={(e) => setPlaybackSpeed(Number(e.target.value))}>
                      <MenuItem value={1}>1x</MenuItem>
                      <MenuItem value={2}>2x</MenuItem>
                      <MenuItem value={4}>4x</MenuItem>
                      <MenuItem value={8}>8x</MenuItem>
                    </Select>
                  </Stack>

                  <Typography variant="textSM"
                    color="text.secondary">Playback simulates a 24h operational day. Tasks & alerts update as time progresses.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        {/* News Carousel - between title section and tasks */}
        <Box sx={{ mt: '24px' }}>
          <NewsCarousel
            pinnedItems={news.pinnedItems}
            otherItems={news.otherItems}
            loading={news.loading}
            error={news.error}
          />
        </Box>
        <Grid container
          spacing={8}
          sx={{ mt: '8px' }}>
          <Grid item
            xs={12}
            lg={8}>
            {operational.loading ? <Skeleton variant="rounded"
              height={220} /> : primaryTask && <UrgentAlertCard task={primaryTask} />}
          </Grid>
          <Grid item
            xs={12}
            lg={4}>
            {operational.loading ? (
              <Skeleton variant="rounded"
                height={220} />
            ) : operational.error ? (
              <Alert severity="error"><AlertTitle>Error</AlertTitle>{operational.error}</Alert>
            ) : (
              <TasksCard
                tasks={remainingTasks}
                taskFilter={taskFilter}
                onTaskFilterChange={setTaskFilter}
                storeFilterDisabled={storeFilterDisabled}
              />
            )}
          </Grid>
        </Grid>
      </PageArea>
      <PageArea backgroundColor={theme.palette.custom.gray[100]}>
        <Grid container
          spacing={6}
          pt={0}>
          <Grid item
            xs={12}
            lg={12}
            pt={'4px !important'}>
            <TimeRangeBar range={range}
              onChange={setRange} />
          </Grid>
          <Grid item
            xs={12}
            lg={6}
            container
            spacing={8}
            sx={{ px: { xs: 2, lg: 4 }, mb: 3 }}>
            {performance.loading
              ? Array.from({ length: 2 }).map((_, k) => (
                <Grid item
                  xs={12}
                  md={12}
                  key={k}>
                  <Skeleton variant="rounded"
                    height={160} />
                </Grid>
              ))
              : performance.metrics?.slice(0, 2).map((m, idx) => (
                <Grid item
                  xs={12}
                  md={12}
                  key={idx}>
                  {m.label === 'Orderly Score' ?
                    <WaveScoreCard metric={m} /> :
                    <MetricCard metric={m} />}
                </Grid>
              ))}

          </Grid>
          <Grid item
            xs={12}
            lg={6}>
            {performance.loading ? <Skeleton variant="rounded"
              height={200} /> : (performance.progress &&
                <ProgressCard good={performance.progress.good}
                  improve={performance.progress.improve} />)}
          </Grid>
          <Grid item
            xs={12}
            lg={12}
            container
            spacing={8}
            sx={{ px: { xs: 2, lg: 4 }, mb: 3 }}>
            {performance.loading
              ? Array.from({ length: (performance.metrics?.length ?? 2) - 2 }).map((_, k) => (
                <Grid item
                  xs={12}
                  md={6}
                  key={k}>
                  <Skeleton variant="rounded"
                    height={160} />
                </Grid>
              ))
              : performance.metrics?.slice(2, performance.metrics?.length).map((m, idx) => (
                <Grid item
                  xs={12}
                  md={6}
                  key={idx}>


                  <MetricCard metric={m} />
                </Grid>
              ))}

          </Grid>
        </Grid>
      </PageArea >
    </>
  );
}
